import {
  endOfMonth,
  endOfWeek,
  isWithinInterval,
  startOfMonth,
  startOfToday,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useAuth from 'contexts/auth/useAuth';
import { airbrake } from 'lib/ErrorBoundary';
import { searchString } from 'lib/stringUtils';
import { normalizeDatetimeToUTC } from 'lib/utils';
import {
  type AskKlueChatSessionProps,
  invalidateAskKlueChatSessions,
  useAskChatSessionsQuery,
} from 'store/api/ask-klue/chat-sessions';
import { useAskKlueThreadId } from 'store/views';

export type ChatSessionGroup = {
  id: string;
  name: string;
  startDate: Date;
  endDate: Date;
  items: AskKlueChatSessionProps[];
};

const getLastMonth = (today: Date, months: number) =>
  startOfMonth(subMonths(today, months));

const getItemsByDateRange = (
  chatSessions: AskKlueChatSessionProps[],
  startDate: Date,
  endDate: Date,
) => {
  return chatSessions.filter((session) => {
    const sessionDate = new Date(normalizeDatetimeToUTC(session.datetime));
    const isWithinRange = isWithinInterval(sessionDate, {
      start: startDate,
      end: endDate,
    });

    return isWithinRange;
  });
};

function useAskKlueChatSessions() {
  const [searchValue, setSearchValue] = useState<string>('');
  const { t } = useTranslation('Common');
  const { isCurator } = useAuth();
  const {
    data: chatSessionsData,
    isLoading,
    isError,
  } = useAskChatSessionsQuery(
    {
      isCurator,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const threadId = useAskKlueThreadId();
  const prevThreadId = useRef(threadId);
  useEffect(() => {
    if (!prevThreadId.current && threadId) {
      // We've started a new chat so we should refetch the history
      invalidateAskKlueChatSessions();
    }
    prevThreadId.current = threadId;
  }, [threadId]);

  const chatSessions = useMemo(() => {
    if (!chatSessionsData?.sessions || !chatSessionsData.sessions.length) {
      return [];
    }

    const sessions = chatSessionsData.sessions;

    const today = startOfToday();
    const startOfThisWeek = startOfWeek(today, { weekStartsOn: 0 });
    const endOfThisWeek = endOfWeek(startOfThisWeek, { weekStartsOn: 0 });
    const startOfLastWeek = subWeeks(startOfThisWeek, 1);
    const endOfLastWeek = endOfWeek(startOfLastWeek, { weekStartsOn: 0 });
    const startOfLastMonth = getLastMonth(today, 1);
    const endOfLastMonth = endOfMonth(startOfLastMonth);
    const startOfLast2Months = getLastMonth(today, 2);
    const endOfLast2Months = endOfMonth(startOfLast2Months);

    const sessionGroups: ChatSessionGroup[] = [
      {
        id: 'this-week',
        name: t('Common:date.thisWeek'),
        startDate: startOfThisWeek,
        endDate: endOfThisWeek,
        items: [],
      },
      {
        id: 'last-week',
        name: t('Common:date.lastWeek'),
        startDate: startOfLastWeek,
        endDate: endOfLastWeek,
        items: [],
      },
      {
        id: 'last-month',
        name: t('Common:date.lastMonth'),
        startDate: startOfLastMonth,
        endDate: endOfLastMonth,
        items: [],
      },
      {
        id: 'last-2-months',
        name: t('Common:date.last2Months'),
        startDate: startOfLast2Months,
        endDate: endOfLast2Months,
        items: [],
      },
    ];

    try {
      return sessionGroups
        .map((group) => {
          const { id, name, startDate, endDate } = group;

          const filteredItems = getItemsByDateRange(
            sessions,
            startDate,
            endDate,
          );
          return {
            id,
            name,
            items: filteredItems,
          };
        })
        .filter((group) => group.items.length > 0)
        .reduce((acc, group) => {
          if (searchValue && group.items && group.items.length > 0) {
            const filteredItems = group.items.filter((item) => {
              return searchString(item.text.toLowerCase() || '', searchValue);
            });

            if (filteredItems.length > 0) {
              acc.push({
                ...group,
                items: filteredItems,
              } as ChatSessionGroup);
            }
          } else if (
            !searchValue ||
            searchString(group.name.toLowerCase() || '', searchValue)
          ) {
            acc.push(group as ChatSessionGroup);
          }

          return acc;
        }, [] as ChatSessionGroup[]);
    } catch (error) {
      airbrake?.notify({
        error: error,
        params: {
          chatSessionsData,
          searchValue,
        },
        context: {
          component: 'askKlueModalSidebar',
          function: 'useAskKlueChatSessions',
        },
      });
      return [];
    }
  }, [chatSessionsData, searchValue, t]);

  return {
    searchValue,
    chatSessions,
    setSearchValue,
    isLoading,
    isError,
  };
}

export default useAskKlueChatSessions;
